/**
 * AIContentHealthCard — top-of-page admin dashboard card that lists
 * every AI-scaffolded course whose lessons haven't all been run through
 * the Generate-content button. Reads GET /admin/lms/ai-content-health.
 *
 * Worst offenders (most empty stubs) surface first so admins fix the
 * biggest gaps before smaller ones. Each row deep-links into the
 * course workspace so the admin can jump straight to the empty
 * lessons.
 */
import React, { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../../App';
import { Card, CardContent } from '../../ui/card';
import { Button } from '../../ui/button';
import { AlertTriangle, CheckCircle2, ChevronDown, ChevronUp, ExternalLink, RefreshCw, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const TIER_STYLES = {
  free:       'bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-200',
  pro:        'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
  business:   'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200',
  enterprise: 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-200',
};

export default function AIContentHealthCard() {
  const { API } = useContext(AuthContext);
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(true);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refilling, setRefilling] = useState({}); // {courseId: bool}

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data: res } = await axios.get(`${API}/admin/lms/ai-content-health`);
      setData(res);
    } catch (e) {
      // Silent — the empty state is still useful and it shouldn't block
      // the rest of the LMS admin from rendering.
    } finally {
      setLoading(false);
    }
  }, [API]);

  useEffect(() => { load(); }, [load]);

  // Trigger the existing "fill-empty-lessons" background job for a
  // course so the admin can kick off catch-up without navigating away.
  const fillEmpty = async (courseId) => {
    setRefilling((r) => ({ ...r, [courseId]: true }));
    try {
      await axios.post(`${API}/admin/lms/courses/${courseId}/fill-empty-lessons`);
      toast.success('Queued — empty lessons will be filled in the background');
      // Refresh after a short delay so the counts start moving.
      setTimeout(load, 4000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not queue fill job');
    } finally {
      setRefilling((r) => ({ ...r, [courseId]: false }));
    }
  };

  if (!data && !loading) return null;
  const totals = data?.totals || {};
  const rows = data?.courses || [];
  const offenders = rows.filter((r) => r.empty_ai_lessons > 0);
  const cleanCount = rows.length - offenders.length;

  return (
    <Card className="mb-4 border-2 border-amber-300 dark:border-amber-700 bg-amber-50/40 dark:bg-amber-900/10" data-testid="ai-content-health-card">
      <CardContent className="p-0">
        <div
          role="button"
          tabIndex={0}
          onClick={() => setExpanded((v) => !v)}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setExpanded((v) => !v); } }}
          className="w-full flex items-center justify-between p-4 hover:bg-amber-100/40 dark:hover:bg-amber-900/20 rounded-t-lg transition-colors cursor-pointer"
          data-testid="ai-content-health-toggle"
        >
          <div className="flex items-center gap-3 flex-wrap min-w-0 text-left">
            {offenders.length > 0 ? (
              <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" />
            ) : (
              <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
            )}
            <div className="min-w-0">
              <div className="font-semibold text-sm text-slate-900 dark:text-white">
                AI Content Health
                {offenders.length > 0 ? (
                  <span className="ml-2 text-amber-800 dark:text-amber-200 font-normal">
                    · {offenders.length} AI course{offenders.length === 1 ? '' : 's'} still shipping empty lesson stubs
                  </span>
                ) : rows.length > 0 ? (
                  <span className="ml-2 text-emerald-800 dark:text-emerald-200 font-normal">
                    · All {rows.length} AI-scaffolded courses fully generated
                  </span>
                ) : (
                  <span className="ml-2 text-slate-600 dark:text-neutral-400 font-normal">
                    · No AI-scaffolded courses yet
                  </span>
                )}
              </div>
              {rows.length > 0 && (
                <div className="text-[11px] text-slate-600 dark:text-neutral-400 mt-0.5">
                  {totals.empty_stub_lessons || 0} empty stub lesson{totals.empty_stub_lessons === 1 ? '' : 's'} ·
                  {' '}{totals.generated_lessons || 0} generated ·
                  {' '}{totals.final_lessons || 0} final ·
                  {' '}{cleanCount} clean course{cleanCount === 1 ? '' : 's'}
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); load(); }}
              className="text-xs text-slate-600 dark:text-neutral-400 hover:text-slate-900 dark:hover:text-white flex items-center gap-1 px-2 py-1 rounded hover:bg-white/70 dark:hover:bg-slate-800/70"
              data-testid="ai-content-health-refresh"
              disabled={loading}
              title="Refresh counts"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </div>
        </div>
        {expanded && rows.length > 0 && (
          <div className="border-t border-amber-200 dark:border-amber-800 p-3 space-y-2" data-testid="ai-content-health-list">
            {rows.map((r) => {
              const pct = r.total_lessons > 0
                ? Math.round((r.generated_lessons / r.total_lessons) * 100)
                : 0;
              const clean = r.empty_ai_lessons === 0;
              return (
                <div
                  key={r.course_id}
                  className={`flex flex-wrap items-center gap-3 p-3 rounded-md border ${
                    clean
                      ? 'border-emerald-200 dark:border-emerald-800 bg-white dark:bg-slate-900'
                      : 'border-amber-300 dark:border-amber-700 bg-white dark:bg-slate-900'
                  }`}
                  data-testid={`ai-content-health-row-${r.course_id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-semibold text-sm text-slate-900 dark:text-white truncate">
                        {r.title || '(untitled)'}
                      </span>
                      <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full uppercase ${TIER_STYLES[r.tier] || TIER_STYLES.free}`}>
                        {r.tier || 'free'}
                      </span>
                      {!r.is_published && (
                        <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200">
                          DRAFT
                        </span>
                      )}
                      {clean ? (
                        <span
                          className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200"
                          data-testid={`ai-content-health-status-${r.course_id}`}
                        >
                          ✓ Complete
                        </span>
                      ) : (
                        <span
                          className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200"
                          data-testid={`ai-content-health-status-${r.course_id}`}
                          title={`${r.empty_ai_lessons} lesson${r.empty_ai_lessons === 1 ? '' : 's'} scaffolded by AI but never generated`}
                        >
                          {r.empty_ai_lessons} empty stub{r.empty_ai_lessons === 1 ? '' : 's'}
                        </span>
                      )}
                    </div>
                    <div className="mt-1.5 flex items-center gap-2">
                      <div className="flex-1 h-1.5 bg-slate-200 dark:bg-slate-700 rounded overflow-hidden max-w-md">
                        <div
                          className={clean ? 'h-full bg-emerald-500' : 'h-full bg-amber-500'}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      <span className="text-[11px] text-slate-600 dark:text-neutral-400 whitespace-nowrap tabular-nums">
                        {r.generated_lessons}/{r.total_lessons} generated · {pct}%
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    {!clean && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => fillEmpty(r.course_id)}
                        disabled={!!refilling[r.course_id]}
                        className="h-7 text-xs border-amber-400 text-amber-800 hover:bg-amber-50 dark:hover:bg-amber-900/20"
                        data-testid={`ai-content-health-fill-${r.course_id}`}
                        title="Queue AI generation for every empty stub lesson in this course"
                      >
                        <Sparkles className="w-3.5 h-3.5 mr-1" />
                        {refilling[r.course_id] ? 'Queuing…' : 'Fill empty'}
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => navigate(`/admin/lms/course/${r.course_id}/workspace`)}
                      className="h-7 text-xs"
                      data-testid={`ai-content-health-open-${r.course_id}`}
                    >
                      <ExternalLink className="w-3.5 h-3.5 mr-1" />
                      Open
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {expanded && rows.length === 0 && !loading && (
          <div className="border-t border-amber-200 dark:border-amber-800 p-4 text-sm text-slate-600 dark:text-neutral-400" data-testid="ai-content-health-empty">
            No AI-scaffolded courses on this site yet. Build one via the "Build with AI" flow above and this dashboard will start tracking its content health.
          </div>
        )}
      </CardContent>
    </Card>
  );
}
